import * as React from 'react';
import Box from '@mui/material/Box';
import { keyframes } from '@mui/system';
import type { BoxProps } from '@mui/material';

export type LoaderSize = 'small' | 'medium' | 'large';

export type LoaderProps = {
  size?: LoaderSize;
  /** Optional exact size override (px). If provided, it wins over `size`. */
  px?: number;
  /** Rotation duration in ms. */
  durationMs?: number;
  className?: string;
  color?: 'default' | 'accent';
} & BoxProps;

const spin = keyframes`
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
`;

const SIZE_PX: Record<LoaderSize, number> = {
  small: 40,
  medium: 72,
  large: 112,
};

export const Loader: React.FC<LoaderProps> = ({
                                                size = 'large',
                                                px,
                                                durationMs = 900,
                                                className,
                                                color = 'default',
                                                sx,
                                                ...rest
                                              }) => {
  const d = px ?? SIZE_PX[size];
  const spinColor = color === 'accent' ? '129, 199, 61' : '255, 255, 255';
  return (
    <Box
      className={className}
      role="status"
      aria-label="Loading"
      sx={{
        width: d,
        height: d,
        display: 'inline-flex',
        flexShrink: 0,
        lineHeight: 0,
        animation: `${spin} ${durationMs}ms linear infinite`,
        transformOrigin: '50% 50%',
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
        ...sx,
      }}
      {...rest}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 112 112"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g
          clipPath="url(#paint0_angular_1576_3343_clip_path)"
          data-figma-skip-parse="true"
        >
          <g transform="matrix(0 -0.0476 0.0476 0 56 56)">
            <foreignObject x="-1410.08" y="-1410.08" width="2820.17" height="2820.17">
              <div
                style={{
                  background:
                    `conic-gradient(from 90deg, rgba(${spinColor}, 0.9956) 0deg, rgba(${spinColor}, 0) 71.2696deg, rgba(${spinColor}, 1) 359.682deg, rgba(${spinColor}, 0.9956) 360deg)`,
                  height: '100%',
                  width: '100%',
                  opacity: 1,
                }}
              />
            </foreignObject>
          </g>
        </g>

        <path
          d="M109.618 39.8417C113.029 51.1585 112.771 63.2643 108.882 74.4258C104.993 85.5872 97.672 95.232 87.9675 101.979C78.2629 108.726 66.6724 112.23 54.8553 111.988C43.0382 111.747 31.6005 107.772 22.1799 100.634C12.7592 93.4959 5.83874 83.5599 2.40919 72.2489C-1.02035 60.9378 -0.783063 48.8316 3.08709 37.6636C6.95724 26.4956 14.2618 16.8385 23.9549 10.0749C33.648 3.31137 45.2327 -0.211801 57.0501 0.00984736L56.9449 5.62153C46.3118 5.4221 35.8883 8.59215 27.1667 14.6778C18.4451 20.7635 11.8726 29.4527 8.39035 39.5014C4.90809 49.5501 4.69458 60.4429 7.7804 70.6203C10.8662 80.7977 17.0931 89.7378 25.5695 96.1606C34.046 102.583 44.3373 106.159 54.97 106.377C65.6027 106.594 76.0316 103.442 84.7635 97.3708C93.4954 91.2999 100.082 82.6218 103.582 72.579C107.081 62.5362 107.313 51.6438 104.244 41.4612L109.618 39.8417Z"
        />

        <defs>
          <clipPath id="paint0_angular_1576_3343_clip_path">
            <path
              d="M109.618 39.8417C113.029 51.1585 112.771 63.2643 108.882 74.4258C104.993 85.5872 97.672 95.232 87.9675 101.979C78.2629 108.726 66.6724 112.23 54.8553 111.988C43.0382 111.747 31.6005 107.772 22.1799 100.634C12.7592 93.4959 5.83874 83.5599 2.40919 72.2489C-1.02035 60.9378 -0.783063 48.8316 3.08709 37.6636C6.95724 26.4956 14.2618 16.8385 23.9549 10.0749C33.648 3.31137 45.2327 -0.211801 57.0501 0.00984736L56.9449 5.62153C46.3118 5.4221 35.8883 8.59215 27.1667 14.6778C18.4451 20.7635 11.8726 29.4527 8.39035 39.5014C4.90809 49.5501 4.69458 60.4429 7.7804 70.6203C10.8662 80.7977 17.0931 89.7378 25.5695 96.1606C34.046 102.583 44.3373 106.159 54.97 106.377C65.6027 106.594 76.0316 103.442 84.7635 97.3708C93.4954 91.2999 100.082 82.6218 103.582 72.579C107.081 62.5362 107.313 51.6438 104.244 41.4612L109.618 39.8417Z" />
          </clipPath>
        </defs>
      </svg>
    </Box>
  );
};